import React from 'react';
import EditorCanvasConsole from '../EditorCanvasConsole';

// Composant pour les sections d'information
export const InfoBox = ({ 
  children, 
  title, 
  color = "blue", // blue, indigo, purple, green, cyan
  className = "" 
}) => {
  const colorVariants = {
    blue: "bg-blue-50 dark:bg-blue-950/30 border-blue-100 dark:border-blue-900/50 text-blue-700 dark:text-blue-300",
    indigo: "bg-indigo-50 dark:bg-indigo-950/30 border-indigo-100 dark:border-indigo-900/50 text-indigo-700 dark:text-indigo-300",
    purple: "bg-purple-50 dark:bg-purple-950/30 border-purple-100 dark:border-purple-900/50 text-purple-700 dark:text-purple-300",
    green: "bg-green-50 dark:bg-green-950/30 border-green-100 dark:border-green-900/50 text-green-700 dark:text-green-300",
    cyan: "bg-cyan-50 dark:bg-cyan-950/30 border-cyan-100 dark:border-cyan-900/50 text-cyan-700 dark:text-cyan-300"
  };

  const colorClasses = colorVariants[color] || colorVariants.blue;

  return (
    <div className={`p-4 rounded-lg border ${colorClasses} ${className} break-words overflow-wrap`}>
      {title && <h3 className="text-lg font-semibold mt-0 break-words">{title}</h3>}
      <div className="text-gray-800 dark:text-white break-words">{children}</div>
    </div>
  );
};

// Composant pour les exercices
export const ExerciseBox = ({ 
  children, 
  title = "Exercice", 
  color = "yellow", // yellow, amber, orange 
  className = "" 
}) => {
  const colorVariants = {
    yellow: "bg-yellow-50 dark:bg-yellow-900/30 border-yellow-100 dark:border-yellow-800/50 text-yellow-700 dark:text-yellow-500",
    amber: "bg-amber-50 dark:bg-amber-900/30 border-amber-100 dark:border-amber-800/50 text-amber-700 dark:text-amber-500",
    orange: "bg-orange-50 dark:bg-orange-900/30 border-orange-100 dark:border-orange-800/50 text-orange-700 dark:text-orange-500"
  };

  const colorClasses = colorVariants[color] || colorVariants.yellow;

  return (
    <div className={`p-4 rounded-lg border ${colorClasses} ${className} break-words overflow-wrap`}>
      <h3 className="text-lg font-semibold mt-0 break-words">{title}</h3>
      <div className="text-gray-800 dark:text-white break-words">{children}</div>
    </div>
  );
};

// Composant pour les notes
export const Note = ({ 
  children, 
  type = "note", // note, warning, tip, orange, gray, exercice
  title,
  className = "" 
}) => {
  const typeClasses = {
    note: "note-container",
    warning: "warning-container",
    tip: "tip-container",
    orange: "orange-container",
    gray: "gray-container",
    exercice: "exercise-container"
  };
  
  return (
    <div className={`${typeClasses[type]} ${className} break-words overflow-wrap`}>
      {title && <h4 className="font-semibold break-words">{title}</h4>}
      <div className="text-gray-800 dark:text-white break-words">{children}</div>
    </div>
  );
};

// Composant pour les blocs de code
export const CodeBlock = ({ 
  children, 
  language = "javascript", 
  className = "",
  noCollapse = false,
  showCopyButton = false // Nouveau paramètre pour forcer l'affichage du bouton copier
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isCopied, setIsCopied] = React.useState(false);
  
  // Compter le nombre de lignes dans le code
  const lines = children ? children.toString().split('\n') : [];
  const AUTO_COLLAPSE_THRESHOLD = 20; // Seuil automatique pour plier le code
  const shouldCollapse = !noCollapse && lines.length > AUTO_COLLAPSE_THRESHOLD;
  
  // Déterminer le contenu à afficher
  const displayContent = shouldCollapse && !isExpanded 
    ? lines.slice(0, AUTO_COLLAPSE_THRESHOLD).join('\n')
    : children;

  // Fonction pour copier le code complet
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(children.toString());
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000); // Reset après 2 secondes
    } catch (err) {
      console.error('Erreur lors de la copie:', err);
    }
  };

      return (
      <div className={`code-block ${className} break-words overflow-wrap relative isolate`}>
      {/* Bouton de copie - visible automatiquement si plus de 20 lignes OU si forcé */}
      {(shouldCollapse || showCopyButton) && (
        <button
          onClick={copyToClipboard}
          className="absolute top-3 right-3 p-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 rounded-md transition-all duration-200 z-[1]"
          title={isCopied ? "Copié !" : "Copier tout le code"}
        >
          {isCopied ? (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          )}
        </button>
      )}
      
      <pre className="text-sm whitespace-pre-wrap break-words">
        <code className={`language-${language}`}>
          {displayContent}
        </code>
      </pre>
      
      {shouldCollapse && (
        <div className="mt-2 text-center">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md text-sm font-medium transition-colors duration-200"
          >
            {isExpanded ? (
              <>
                ↑ Réduire le code ({lines.length} lignes)
              </>
            ) : (
              <>
                ↓ Voir tout le code ({lines.length} lignes)
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

// Composant pour le code inline
export const InlineCode = ({ children, className = "" }) => {
  return (
    <span className={`code-inline ${className}`}>
      <code>{children}</code>
    </span>
  );
};

// Composant pour colorer le texte en rouge ou en vert dans les blocs de code
export const ColoredText = ({ 
  children, 
  color = "green", // green, red
  className = "" 
}) => {
  const colorClasses = {
    green: "text-green-600 dark:text-green-400",
    red: "text-red-600 dark:text-red-400"
  };

  const colorClass = colorClasses[color] || colorClasses.green;

  return (
    <span className={`${colorClass} ${className}`}>
      {children}
    </span>
  );
};

// Composant pour les éditeurs de code
export const CodeEditor = ({
  initialCode,
  courseId,
  editorId,
  title,
  showConsole = true,
  height = "300px",
  autoFocus = false,
  allowCopyPaste = false, // Nouvelle prop pour autoriser le copier-coller
  editorCols = 7, // Nombre de colonnes sur 12 occupées par l'éditeur (8/12 par défaut)
  className = ""
}) => {
  const canvasId = `${courseId}_${editorId}`;
  
  return (
    <div className={`editor-container ${className}`}>
      <h3 className="section-title">{title}</h3>
      <EditorCanvasConsole
        initialCode={initialCode}
        canvasId={canvasId}
        title={title}
        showConsole={showConsole}
        height={height}
        autoFocus={autoFocus}
        allowCopyPaste={allowCopyPaste}
        editorCols={editorCols}
      />
    </div>
  );
};

// Composant pour les tableaux
export const TableContainer = ({ children, className = "" }) => {
  return (
    <div className={`table-container ${className} break-words overflow-wrap`}>
      <div className="text-gray-800 dark:text-white break-words overflow-x-auto">{children}</div>
    </div>
  );
};

// Composant pour les titres de section
export const SectionTitle = ({ children, className = "" }) => {
  return (
    <h3 className={`section-title ${className}`}>{children}</h3>
  );
};

// Composant de mise en évidence
export const Highlight = ({ children, className = "" }) => {
  return (
    <span className={`highlight-block ${className}`}>{children}</span>
  );
};

// Composant avec image de fond
export const PatternContainer = ({ children, className = "" }) => {
  return (
    <div className={`pattern-container ${className}`}>
      <div className="text-gray-800 dark:text-white">{children}</div>
    </div>
  );
};

// Composant pour les titres de section principaux
export const SectionHeading = ({ children, className = "" }) => {
  return (
    <h2 className={`text-2xl font-bold mb-6 ${className}`}>{children}</h2>
  );
};

// Composant pour les sous-titres
export const SubHeading = ({ children, className = "" }) => {
  return (
    <h3 className={`text-xl font-semibold mt-8 mb-4 ${className}`}>{children}</h3>
  );
};

// Composant de grille à deux colonnes
export const TwoColGrid = ({ children, className = "" }) => {
  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 ${className}`}>
      <div className="text-gray-800 dark:text-white">{children}</div>
    </div>
  );
};

// Composant de grille à trois colonnes
export const ThreeColGrid = ({ children, className = "" }) => {
  return (
    <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 ${className}`}>
      <div className="text-gray-800 dark:text-white">{children}</div>
    </div>
  );
};

// Composant d'élément de grille
export const GridItem = ({ children, colSpan = 1, className = "" }) => {
  return (
    <div className={`col-span-1 md:col-span-${colSpan} ${className}`}>
      <div className="text-gray-800 dark:text-white">{children}</div>
    </div>
  );
};

// Composant de séparateur
export const Divider = ({ className = "" }) => {
  return (
    <hr className={`my-10 border-t-2 border-gray-200 dark:border-gray-800 ${className}`} />
  );
};

// Composant pour le titre principal du cours
export const CourseTitle = ({ children, className = "" }) => {
  return (
    <h1 className={`text-4xl font-bold text-center mb-8 text-gray-900 dark:text-white ${className}`}>
      {children}
    </h1>
  );
};

// Composant pour les titres de chapitre
export const ChapterTitle = ({ children, number, className = "" }) => {
  return (
    <div className={`mb-8 ${className}`}>
      {number && (
        <div className="text-sm font-medium text-blue-600 dark:text-blue-400 mb-2">
          Chapitre {number}
        </div>
      )}
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
        {children}
      </h1>
    </div>
  );
};

// Composant pour les titres de leçon
export const LessonTitle = ({ children, number, className = "" }) => {
  return (
    <div className={`mb-6 ${className}`}>
      {number && (
        <div className="text-sm font-medium text-indigo-600 dark:text-indigo-400 mb-2">
          Leçon {number}
        </div>
      )}
      <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
        {children}
      </h2>
    </div>
  );
};

// Composant pour les sous-sections avec numérotation
export const SubSection = ({ children, number, title, className = "" }) => {
  return (
    <div className={`mb-6 ${className}`}>
      <div className="flex items-center mb-3">
        {number && (
          <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-purple-600 rounded-full mr-3">
            {number}
          </span>
        )}
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
          {title}
        </h4>
      </div>
      <div className="ml-9 text-gray-800 dark:text-white">
        {children}
      </div>
    </div>
  );
};

// Composant pour les sections avec icône
export const IconSection = ({ children, icon, title, color = "blue", className = "" }) => {
  const colorVariants = {
    blue: "text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30",
    green: "text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30",
    purple: "text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/30",
    orange: "text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/30",
    red: "text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30"
  };

  const colorClasses = colorVariants[color] || colorVariants.blue;

  return (
    <div className={`mb-6 ${className}`}>
      <div className="flex items-center mb-4">
        {icon && (
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 ${colorClasses}`}>
            <span className="text-lg">{icon}</span>
          </div>
        )}
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
          {title}
        </h3>
      </div>
      <div className="text-gray-800 dark:text-white">
        {children}
      </div>
    </div>
  );
};

// Composant pour les titres avec ligne décorative
export const DecorativeTitle = ({ children, color = "blue", className = "" }) => {
  const colorVariants = {
    blue: "bg-blue-500",
    green: "bg-green-500",
    purple: "bg-purple-500",
    orange: "bg-orange-500",
    red: "bg-red-500"
  };

  const bgColor = colorVariants[color] || colorVariants.blue;

  return (
    <div className={`mb-8 ${className}`}>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        {children}
      </h2>
      <div className={`w-16 h-1 ${bgColor} rounded`}></div>
    </div>
  );
};

// Composant pour les sections pliables/dépliables
export const CollapsibleSection = ({ children, title, defaultOpen = false, className = "" }) => {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);

  return (
    <div className={`border border-gray-200 dark:border-gray-700 rounded-lg mb-4 ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 text-left bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-t-lg flex items-center justify-between"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {title}
        </h3>
        <span className="text-gray-500 dark:text-gray-400">
          {isOpen ? '−' : '+'}
        </span>
      </button>
      {isOpen && (
        <div className="p-4 text-gray-800 dark:text-white break-words overflow-wrap">
          {children}
        </div>
      )}
    </div>
  );
};

// Composant pour les titres avec badge
export const BadgeTitle = ({ children, badge, badgeColor = "blue", className = "" }) => {
  const badgeColorVariants = {
    blue: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
    green: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
    purple: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
    orange: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300",
    red: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
  };

  const badgeClasses = badgeColorVariants[badgeColor] || badgeColorVariants.blue;

  return (
    <div className={`flex items-center gap-3 mb-6 ${className}`}>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
        {children}
      </h2>
      {badge && (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${badgeClasses}`}>
          {badge}
        </span>
      )}
    </div>
  );
};

// Composant pour les titres entièrement soulignés
export const UnderlinedTitle = ({ 
  children, 
  level = "h2", // h1, h2, h3, h4
  color = "blue", 
  thickness = "2", // 1, 2, 3, 4
  style = "solid", // solid, double, dashed, dotted
  className = "" 
}) => {
  const colorVariants = {
    blue: "border-blue-500 dark:border-blue-400",
    green: "border-green-500 dark:border-green-400",
    purple: "border-purple-500 dark:border-purple-400",
    orange: "border-orange-500 dark:border-orange-400",
    red: "border-red-500 dark:border-red-400",
    gray: "border-gray-500 dark:border-gray-400",
    indigo: "border-indigo-500 dark:border-indigo-400",
    cyan: "border-cyan-500 dark:border-cyan-400"
  };

  const levelStyles = {
    h1: "text-3xl font-bold",
    h2: "text-2xl font-bold", 
    h3: "text-xl font-semibold",
    h4: "text-lg font-semibold"
  };

  const thicknessVariants = {
    "1": "border-b",
    "2": "border-b-2",
    "3": "border-b-4",
    "4": "border-b-8"
  };

  const styleVariants = {
    solid: "",
    double: "border-double",
    dashed: "border-dashed",
    dotted: "border-dotted"
  };

  const colorClasses = colorVariants[color] || colorVariants.blue;
  const levelClasses = levelStyles[level] || levelStyles.h2;
  const thicknessClasses = thicknessVariants[thickness] || thicknessVariants["2"];
  const styleClasses = styleVariants[style] || styleVariants.solid;

  const Tag = level;

  return (
    <Tag className={`${levelClasses} text-gray-900 dark:text-white mb-6 pb-2 ${thicknessClasses} ${styleClasses} ${colorClasses} ${className}`}>
      {children}
    </Tag>
  );
};

// Composant pour les titres avec soulignement partiel
export const PartialUnderlinedTitle = ({ 
  children, 
  level = "h2",
  color = "blue", 
  underlineWidth = "50", // 25, 50, 75, 100 (en pourcentage)
  className = "" 
}) => {
  const colorVariants = {
    blue: "bg-blue-500 dark:bg-blue-400",
    green: "bg-green-500 dark:bg-green-400",
    purple: "bg-purple-500 dark:bg-purple-400",
    orange: "bg-orange-500 dark:bg-orange-400",
    red: "bg-red-500 dark:bg-red-400",
    gray: "bg-gray-500 dark:bg-gray-400",
    indigo: "bg-indigo-500 dark:bg-indigo-400",
    cyan: "bg-cyan-500 dark:bg-cyan-400"
  };

  const levelStyles = {
    h1: "text-3xl font-bold",
    h2: "text-2xl font-bold", 
    h3: "text-xl font-semibold",
    h4: "text-lg font-semibold"
  };

  const widthVariants = {
    "25": "w-1/4",
    "50": "w-1/2", 
    "75": "w-3/4",
    "100": "w-full"
  };

  const colorClasses = colorVariants[color] || colorVariants.blue;
  const levelClasses = levelStyles[level] || levelStyles.h2;
  const widthClasses = widthVariants[underlineWidth] || widthVariants["50"];

  const Tag = level;

  return (
    <div className={`mb-6 ${className}`}>
      <Tag className={`${levelClasses} text-gray-900 dark:text-white mb-2`}>
        {children}
      </Tag>
      <div className={`h-1 ${widthClasses} ${colorClasses} rounded`}></div>
    </div>
  );
};

// Composant pour les titres avec soulignement animé
export const AnimatedUnderlinedTitle = ({ 
  children, 
  level = "h2",
  color = "blue",
  animationType = "expand", // expand, slide, fade
  className = "" 
}) => {
  const colorVariants = {
    blue: "bg-blue-500 dark:bg-blue-400",
    green: "bg-green-500 dark:bg-green-400",
    purple: "bg-purple-500 dark:bg-purple-400",
    orange: "bg-orange-500 dark:bg-orange-400",
    red: "bg-red-500 dark:bg-red-400",
    gray: "bg-gray-500 dark:bg-gray-400",
    indigo: "bg-indigo-500 dark:bg-indigo-400",
    cyan: "bg-cyan-500 dark:bg-cyan-400"
  };

  const levelStyles = {
    h1: "text-3xl font-bold",
    h2: "text-2xl font-bold", 
    h3: "text-xl font-semibold",
    h4: "text-lg font-semibold"
  };

  const animationVariants = {
    expand: "hover:w-full transition-all duration-300 ease-in-out w-0",
    slide: "hover:translate-x-0 transition-transform duration-300 ease-in-out -translate-x-full",
    fade: "hover:opacity-100 transition-opacity duration-300 ease-in-out opacity-50"
  };

  const colorClasses = colorVariants[color] || colorVariants.blue;
  const levelClasses = levelStyles[level] || levelStyles.h2;
  const animationClasses = animationVariants[animationType] || animationVariants.expand;

  const Tag = level;

  return (
    <div className={`mb-6 group ${className}`}>
      <Tag className={`${levelClasses} text-gray-900 dark:text-white mb-2 cursor-pointer`}>
        {children}
      </Tag>
      <div className={`h-1 ${colorClasses} rounded ${animationClasses}`}></div>
    </div>
  );
};

// Composant séparateur avancé
export const Separator = ({ 
  type = "line", // line, dots, dashed, gradient, text
  color = "gray", 
  size = "md", // sm, md, lg
  text,
  noPaddingTop = false, // nouvelle prop pour enlever le padding du haut
  className = "" 
}) => {
  const colorVariants = {
    // Gris
    gray: "border-gray-200 dark:border-gray-700 bg-gray-200 dark:bg-gray-700",
    "gray-light": "border-gray-100 dark:border-gray-800 bg-gray-100 dark:bg-gray-800",
    "gray-dark": "border-gray-400 dark:border-gray-500 bg-gray-400 dark:bg-gray-500",
    "gray-lighter": "border-gray-50 dark:border-gray-900 bg-gray-50 dark:bg-gray-900",
    "gray-darker": "border-gray-600 dark:border-gray-400 bg-gray-600 dark:bg-gray-400",
    
    // Bleus
    blue: "border-blue-300 dark:border-blue-600 bg-blue-300 dark:bg-blue-600",
    "blue-light": "border-blue-200 dark:border-blue-700 bg-blue-200 dark:bg-blue-700",
    "blue-dark": "border-blue-500 dark:border-blue-400 bg-blue-500 dark:bg-blue-400",
    
    // Verts
    green: "border-green-300 dark:border-green-600 bg-green-300 dark:bg-green-600",
    "green-light": "border-green-200 dark:border-green-700 bg-green-200 dark:bg-green-700",
    "green-dark": "border-green-500 dark:border-green-400 bg-green-500 dark:bg-green-400",
    
    // Violets
    purple: "border-purple-300 dark:border-purple-600 bg-purple-300 dark:bg-purple-600",
    "purple-light": "border-purple-200 dark:border-purple-700 bg-purple-200 dark:bg-purple-700",
    "purple-dark": "border-purple-500 dark:border-purple-400 bg-purple-500 dark:bg-purple-400",
    
    // Oranges
    orange: "border-orange-300 dark:border-orange-600 bg-orange-300 dark:bg-orange-600",
    "orange-light": "border-orange-200 dark:border-orange-700 bg-orange-200 dark:bg-orange-700",
    "orange-dark": "border-orange-500 dark:border-orange-400 bg-orange-500 dark:bg-orange-400",
    
    // Rouges
    red: "border-red-300 dark:border-red-600 bg-red-300 dark:bg-red-600",
    "red-light": "border-red-200 dark:border-red-700 bg-red-200 dark:bg-red-700",
    "red-dark": "border-red-500 dark:border-red-400 bg-red-500 dark:bg-red-400",
    
    // Indigos
    indigo: "border-indigo-300 dark:border-indigo-600 bg-indigo-300 dark:bg-indigo-600",
    "indigo-light": "border-indigo-200 dark:border-indigo-700 bg-indigo-200 dark:bg-indigo-700",
    "indigo-dark": "border-indigo-500 dark:border-indigo-400 bg-indigo-500 dark:bg-indigo-400",
    
    // Cyans
    cyan: "border-cyan-300 dark:border-cyan-600 bg-cyan-300 dark:bg-cyan-600",
    "cyan-light": "border-cyan-200 dark:border-cyan-700 bg-cyan-200 dark:bg-cyan-700",
    "cyan-dark": "border-cyan-500 dark:border-cyan-400 bg-cyan-500 dark:bg-cyan-400",
    
    // Roses
    pink: "border-pink-300 dark:border-pink-600 bg-pink-300 dark:bg-pink-600",
    "pink-light": "border-pink-200 dark:border-pink-700 bg-pink-200 dark:bg-pink-700",
    "pink-dark": "border-pink-500 dark:border-pink-400 bg-pink-500 dark:bg-pink-400",
    
    // Jaunes
    yellow: "border-yellow-300 dark:border-yellow-600 bg-yellow-300 dark:bg-yellow-600",
    "yellow-light": "border-yellow-200 dark:border-yellow-700 bg-yellow-200 dark:bg-yellow-700",
    "yellow-dark": "border-yellow-500 dark:border-yellow-400 bg-yellow-500 dark:bg-yellow-400",
    
    // Emeraudes
    emerald: "border-emerald-300 dark:border-emerald-600 bg-emerald-300 dark:bg-emerald-600",
    "emerald-light": "border-emerald-200 dark:border-emerald-700 bg-emerald-200 dark:bg-emerald-700",
    "emerald-dark": "border-emerald-500 dark:border-emerald-400 bg-emerald-500 dark:bg-emerald-400",
    
    // Teals
    teal: "border-teal-300 dark:border-teal-600 bg-teal-300 dark:bg-teal-600",
    "teal-light": "border-teal-200 dark:border-teal-700 bg-teal-200 dark:bg-teal-700",
    "teal-dark": "border-teal-500 dark:border-teal-400 bg-teal-500 dark:bg-teal-400"
  };

  const sizeVariants = {
    sm: "my-4",
    md: "my-6",
    lg: "my-8"
  };

  const colorClasses = colorVariants[color] || colorVariants.gray;
  const sizeClasses = sizeVariants[size] || sizeVariants.md;
  
  // Gestion du padding conditionnel
  const paddingClasses = noPaddingTop ? "pb-8" : "py-8";

  if (type === "text" && text) {
    return (
      <div className={`flex items-center ${paddingClasses} ${sizeClasses} ${className}`}>
        <div className={`flex-1 h-px ${colorClasses}`}></div>
        <span className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-white bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          {text}
        </span>
        <div className={`flex-1 h-px ${colorClasses}`}></div>
      </div>
    );
  }

  if (type === "dots") {
    return (
      <div className={`flex justify-center items-center ${paddingClasses} ${sizeClasses} ${className}`}>
        <div className="flex space-x-2">
          <div className={`w-2 h-2 rounded-full ${colorClasses}`}></div>
          <div className={`w-2 h-2 rounded-full ${colorClasses}`}></div>
          <div className={`w-2 h-2 rounded-full ${colorClasses}`}></div>
        </div>
      </div>
    );
  }

  if (type === "dashed") {
    return (
      <div className={`${paddingClasses} ${sizeClasses} ${className}`}>
        <hr className={`border-t-2 border-dashed ${colorClasses}`} />
      </div>
    );
  }

  if (type === "gradient") {
    return (
      <div className={`${paddingClasses} ${sizeClasses} ${className}`}>
        <div className="h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent"></div>
      </div>
    );
  }

  // Type "line" par défaut
  return (
    <div className={`${paddingClasses} ${sizeClasses} ${className}`}>
      <hr className={`border-t ${colorClasses}`} />
    </div>
  );
};

// Composant pour le texte en petit et muted (grisé)
export const MutedText = ({ 
  children, 
  size = "base", // xs, sm, base
  className = "" 
}) => {
  const sizeVariants = {
    xs: "text-xs",
    sm: "text-sm", 
    base: "text-base"
  };

  const sizeClasses = sizeVariants[size] || sizeVariants.sm;

  return (
    <span className={`${sizeClasses} text-gray-500 dark:text-gray-400 ${className}`}>
      {children}
    </span>
  );
};

// Composant pour le texte en gras
export const BoldText = ({ 
  children, 
  className = "" 
}) => {
  return (
    <span className={`font-bold ${className}`}>
      {children}
    </span>
  );
}; 

export const BoldTextParagraph = ({ 
  children, 
  className = "" 
}) => {
  return (
    <p className={`font-semibold ${className}`}>
      {children}
    </p>
  );
}; 

// Composant pour afficher le résultat attendu d'un exercice
export const Result = ({ 
  children, 
  className = "" 
}) => {
  return (
    <span className={`text-blue-600 dark:text-blue-400 font-medium ${className}`}>
      🎯 Résultat attendu : {children}
    </span>
  );
};

// Composant pour les corrections d'exercices (pliable)
export const CorrectionBox = ({ 
  children, 
  title = "💡 Voir la correction",
  className = "" 
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [showEncouragement, setShowEncouragement] = React.useState(true);

  const handleClick = () => {
    if (!isOpen) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
      // Réinitialiser l'encouragement si on ferme puis rouvre
      setShowEncouragement(true);
    }
  };

  const handleTryFirst = () => {
    setShowEncouragement(false);
  };

  return (
    <div className={`border border-green-200 dark:border-green-700 rounded-lg mt-4 ${className}`}>
      <button
        onClick={handleClick}
        className="w-full px-4 py-3 text-left bg-green-50 dark:bg-green-900/30 hover:bg-green-100 dark:hover:bg-green-800/40 rounded-lg flex items-center justify-between transition-colors duration-200"
      >
        <span className="text-sm font-medium text-green-700 dark:text-green-300">
          {title}
        </span>
        <span className="text-green-500 dark:text-green-400 font-bold">
          {isOpen ? '−' : '+'}
        </span>
      </button>
      {isOpen && (
        <div className="p-4 bg-green-50/50 dark:bg-green-900/10 rounded-b-lg border-t border-green-200 dark:border-green-700">
          {showEncouragement ? (
            <div className="text-center">
              <div className="text-orange-600 dark:text-orange-400 font-medium mb-3">
                🤔 Avant de regarder la correction, essaie de réfléchir par toi-même !
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                L'apprentissage est plus efficace quand tu cherches d'abord la solution. As-tu essayé de coder dans l'éditeur ?
              </p>
              <button
                onClick={handleTryFirst}
                className="px-4 py-2 bg-green-600 dark:bg-green-500 text-white rounded-lg hover:bg-green-700 dark:hover:bg-green-600 transition-colors text-sm font-medium"
              >
                ✅ J'ai essayé, montrer la correction
              </button>
            </div>
          ) : (
            <div className="text-gray-800 dark:text-white break-words overflow-wrap">
              {children}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Composant pour afficher une image avec bordures arrondies
export const Image = ({ 
  src, 
  alt = "", 
  width = "auto", 
  height = "auto",
  borderRadius = "md", // sm, md, lg, xl, 2xl, full
  shadow = false, // true pour ajouter une ombre
  border = false, // true pour ajouter une bordure
  className = "" 
}) => {
  const borderRadiusVariants = {
    none: "rounded-none",
    sm: "rounded-sm",
    md: "rounded-md", 
    lg: "rounded-lg",
    xl: "rounded-xl",
    "2xl": "rounded-2xl",
    "3xl": "rounded-3xl",
    full: "rounded-full"
  };

  const radiusClasses = borderRadiusVariants[borderRadius] || borderRadiusVariants.md;
  const shadowClasses = shadow ? "shadow-lg" : "";
  const borderClasses = border ? "border border-gray-200 dark:border-gray-700" : "";

  const styleProps = {
    width: width !== "auto" ? width : undefined,
    height: height !== "auto" ? height : undefined
  };

  return (
    <img
      src={src}
      alt={alt}
      style={styleProps}
      className={`${radiusClasses} ${shadowClasses} ${borderClasses} ${className}`}
    />
  );
};

// Composant pour afficher une image centrée avec caption
export const ImageWithCaption = ({ 
  src, 
  alt = "", 
  caption,
  width = "auto", 
  height = "auto",
  borderRadius = "md",
  shadow = true,
  border = false,
  className = "" 
}) => {
  return (
    <div className={`text-center my-6 ${className}`}>
      <Image
        src={src}
        alt={alt}
        width={width}
        height={height}
        borderRadius={borderRadius}
        shadow={shadow}
        border={border}
        className="mx-auto"
      />
      {caption && (
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 italic">
          {caption}
        </p>
      )}
    </div>
  );
};
